import React, { useEffect, useMemo } from 'react';

import { getStringWithoutHash } from 'utilitys/helpers/general';
import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useConfigurationStore } from 'store/configurationsStore';
import { usePhasePerformanceStore } from 'store/phasePerformance.store';

import { useQueryPhasePerformanceGetContracts } from 'api/queries/getMappedContracts.query';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { prepareVersionSelectTitle } from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.utils';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { LoaderContainer } from 'components/common';
import {
    IPhasePerformanceProject,
    IProjectStatus,
} from 'components/widgets/PhasePerformanceChart/PhasePerformance.types';
import { getFiltered, getMonthsDuration } from 'components/widgets/PhasePerformanceChart/PhasePerformance.utils';
import classes from 'components/widgets/PhasePerformanceChart/PhasePerformanceChart.module.scss';
import { ProjectRow } from 'components/widgets/PhasePerformanceChart/components/ProjectRaw/ProjectRow';
import { PhasePerformanceGrid } from 'components/widgets/PhasePerformanceChart/components/phasePerformanceGrid/phasePerformanceGrid';

export interface Props extends IComponentProps {
    widgetId?: string;
    localFilters?: {
        templateId: number;
        selectedProject: IPhasePerformanceProject[];
        selectedStatus: ISelectOption<IProjectStatus>[];
        selectedGates: ISelectOption<number> | null;
        selectedTags: ISelectOption<number>[];
        templateName: string;
    };
}

export const PhasePerformanceChart = ({
    widgetId,
    localFilters,
    editNarrative = null,
    externalComponents = null,
    setSubTitleForExport,
    widgetViewMode = WidgetView.page,
}: Props) => {
    const {
        name: titleFromConfig,
        routeId: id,
        componentKey,
        endpoints,
    } = useConfigurationStore().phasePerformanceChart;
    const endpoint = endpoints.getMappedContracts;

    const { template, selectedProjects, selectedStatus, selectedGates, selectedTags } = usePhasePerformanceStore(
        (store) => ({
            template: store.template,
            selectedProjects: store.selectedProjects,
            selectedStatus: store.selectedStatus,
            selectedGates: store.selectedGates,
            selectedTags: store.selectedTags,
        }),
    );

    const templateId = template?.value;
    const templateName = template?.label;

    const filters = useMemo(
        () =>
            getFiltersData({
                CONTRACT_PHASE_PERFORMANCE:
                    widgetId && localFilters
                        ? localFilters.selectedProject?.map((item) => item.contract_id)
                        : selectedProjects?.map((item) => item.contract_id),
                STATUS_PHASE_PERFORMANCE:
                    widgetId && localFilters
                        ? localFilters.selectedStatus?.map((item) => item.value)
                        : selectedStatus?.map((item) => item.value),
                TAG_PHASE_PERFORMANCE:
                    widgetId && localFilters
                        ? localFilters.selectedTags?.map((item) => item.value)
                        : selectedTags?.map((item) => item.value),
            }),

        [widgetId, localFilters, selectedProjects, selectedStatus, selectedTags],
    );

    const { data: contractsData, isLoading } = useQueryPhasePerformanceGetContracts(
        {
            template_id: widgetId && localFilters ? localFilters.templateId : templateId,
            filter: {
                filters,
            },
        },
        endpoint,
    );

    const { generateFilters: formattedFilter } = useGlobalFiltersHook({
        filter: {
            filters: [
                { name: 'Projects', data: localFilters?.selectedProject?.map((item) => item.name) },
                { name: 'Phase', data: localFilters?.selectedGates?.label ? [localFilters?.selectedGates?.label] : [] },
                { name: 'Status', data: localFilters?.selectedStatus?.map((item) => item.label) },
                { name: 'Tags', data: localFilters?.selectedTags?.map((item) => item.label) },
            ],
        },
    });

    const filtered = getFiltered({
        contracts: contractsData?.mapped_contracts,
        selectedGates: localFilters?.selectedGates || selectedGates,
    });

    const monthsDuration = filtered.length ? getMonthsDuration(filtered) : 0;

    const route = `${getStringWithoutHash(window.location.href)}#${id}`;
    const title = `${titleFromConfig} ${localFilters?.templateName || templateName}`;
    const subTitle: string = prepareVersionSelectTitle();

    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle, title });
    }, [widgetId, subTitle]);

    if (!template) {
        return null;
    }
    return (
        <WidgetWithTitle
            title={<WidgetTitle title={title} widgetViewMode={widgetViewMode} />}
            id={id}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    widgetId={widgetId}
                    route={route}
                    localFilters={{
                        templateId,
                        selectedProjects,
                        selectedStatus,
                        selectedGates,
                        selectedTags,
                        templateName,
                    }}
                    shouldHide={true}
                />,
                <ExportDropdown key={'export'} title={title} subTitle={subTitle} />,
                <TooltipIcon key={'info'} tooltip={contractsData?.phase_performance_chart_info} />,
                <ThreeDotsMenu
                    widgetViewMode={widgetViewMode}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    shareTitle={title}
                    shareLink={route}
                />,
            ]}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && formattedFilter.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} filters={formattedFilter} />
                ) : null,
            ]}
        >
            <>
                <div className={classes.wrapper}>
                    <div className={classes.box}>
                        <div className={classes.container}>
                            {isLoading && (
                                <LoaderContainer height={'500px'}>
                                    <OverlayWithSpinner />
                                </LoaderContainer>
                            )}
                            {!isLoading && filtered.length === 0 && <NoData />}
                            {!isLoading && filtered.length > 0 && (
                                <>
                                    <PhasePerformanceGrid monthsDuration={monthsDuration} />
                                    {filtered.map((row, index) => {
                                        return Boolean(row.error) ? null : (
                                            <ProjectRow
                                                key={row.contract_id + index}
                                                row={row}
                                                monthsDuration={monthsDuration}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </>
        </WidgetWithTitle>
    );
};
